import { useState, useEffect } from 'react';
import { RefreshCw, Smartphone } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { useSelector } from 'react-redux';

function QrDisplay({ code }) {
  const blocks = Array.from({ length: 25 }, (_, i) => (parseInt(code, 36) >> (i % 20)) & 1);
  return (
    <div className="relative">
      <div className="grid grid-cols-10 gap-1 p-6 bg-white rounded-[16px] shadow-inner" style={{ width: 260, height: 260 }}>
        {Array.from({ length: 100 }).map((_, i) => {
          const row = Math.floor(i / 10), col = i % 10;
          const isBorder = row === 0 || row === 9 || col === 0 || col === 9;
          const isCorner = (row < 3 && col < 3) || (row < 3 && col > 6) || (row > 6 && col < 3);
          const isData = blocks[(row * 10 + col) % 25];
          return (
            <div key={i} className={`rounded-[2px] ${
              isCorner ? 'bg-gray-900' :
              isBorder ? 'bg-gray-800' :
              isData ? 'bg-gray-900' : 'bg-transparent'
            }`}/>
          );
        })}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white px-3 py-1.5 rounded-[8px] shadow">
          <p className="text-[18px] font-black text-gray-900 tracking-[0.3em]">{code}</p>
        </div>
      </div>
    </div>
  );
}

export default function AttendanceQrPage() {
  const company = useSelector(s => s.company);
  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(30);

  const generateCode = () => {
    const ts = Math.floor(Date.now() / 30000);
    const raw = (ts * 999983 + (company?.id?.charCodeAt(0) || 7)) % 1000000;
    setCode(String(raw).padStart(6, '0'));
    setSeconds(30 - (Math.floor(Date.now() / 1000) % 30));
  };

  useEffect(() => {
    generateCode();
    const interval = setInterval(() => {
      const rem = 30 - (Math.floor(Date.now() / 1000) % 30);
      setSeconds(rem);
      if (rem === 30) generateCode();
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const pct = (seconds / 30) * 100;
  const isExpiring = seconds <= 8;

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="QR Attendance"
        subtitle="Display this code at the entrance for employees to scan"
        breadcrumb={[{ label: 'Attendance', href: '/attendance' }, { label: 'QR Code' }]}
      />

      <div className="max-w-md mx-auto space-y-6">
        <div className="card flex flex-col items-center gap-6 py-8">
          <QrDisplay code={code}/>

          <div className="w-full space-y-2">
            <div className="flex justify-between text-[12px]">
              <span className={isExpiring ? 'text-red-500 font-bold' : 'text-[var(--text-secondary)]'}>
                {isExpiring ? '⚠ Expiring soon' : 'Refreshes in'}
              </span>
              <span className={`font-bold text-[14px] ${isExpiring ? 'text-red-500' : 'text-[var(--text-primary)]'}`}>{seconds}s</span>
            </div>
            <div className="h-2 bg-[var(--bg-muted)] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-1000 ${isExpiring ? 'bg-red-500' : 'bg-[var(--accent)]'}`}
                style={{ width: `${pct}%` }}/>
            </div>
          </div>

          <button onClick={generateCode} className="flex items-center gap-2 text-[13px] text-[var(--accent)] font-semibold hover:underline">
            <RefreshCw size={14}/> Refresh Now
          </button>
        </div>

        <div className="card space-y-3 p-5">
          <div className="flex items-center gap-2">
            <Smartphone size={16} className="text-[var(--accent)]"/>
            <p className="text-[14px] font-bold text-[var(--text-primary)]">How it works</p>
          </div>
          <ol className="space-y-2 text-[13px] text-[var(--text-secondary)]">
            <li className="flex gap-2"><span className="font-bold text-[var(--accent)] shrink-0">1.</span>Employee opens the Staff App on their phone</li>
            <li className="flex gap-2"><span className="font-bold text-[var(--accent)] shrink-0">2.</span>Taps "QR Check-in" and scans this code</li>
            <li className="flex gap-2"><span className="font-bold text-[var(--accent)] shrink-0">3.</span>GPS location is captured automatically</li>
            <li className="flex gap-2"><span className="font-bold text-[var(--accent)] shrink-0">4.</span>Attendance is marked — code changes every 30 seconds</li>
          </ol>
          <p className="text-[11px] text-[var(--text-tertiary)] pt-1">
            Each code is single-use and GPS-bound. Cannot be shared via screenshot.
          </p>
        </div>

        <div className="card bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
          <p className="text-[12px] font-semibold text-amber-700 dark:text-amber-400">Phase 1 — Test Mode</p>
          <p className="text-[12px] text-amber-600 dark:text-amber-500 mt-0.5">
            QR scanning requires the Staff App (Phase 1D). Current code is display-only. Full GPS-bound QR validation activates when the Staff App is live.
          </p>
        </div>
      </div>
    </div>
  );
}
